/**
 * @file components/JsonViewer.jsx
 * @description Collapsible tree viewer for JSON strings, used to render
 * request/response bodies in the Logs page. Falls back to plain text if the
 * value isn't valid JSON.
 *
 * Uses a custom fixed-width caret column (instead of native <details>/<summary>
 * markers) so key labels line up regardless of whether a row is expandable —
 * the native marker only appears on expandable rows, which pushed those rows'
 * text out of alignment with sibling primitive rows that have no marker.
 */

import React, { useState } from 'react';

const TYPE_COLOR = {
  string: 'text-success',
  number: 'text-danger',
  boolean: 'text-warning-emphasis',
  null: 'text-muted',
};

/** Width reserved for the caret/spacer column so every row's label starts at the same x-offset. */
const CARET_WIDTH = 14;

/** Renders a single primitive value (string/number/boolean/null) with a key label. */
function JsonPrimitive({ name, value, type }) {
  return (
    <div className="d-flex">
      <span style={{ width: CARET_WIDTH, flexShrink: 0 }} aria-hidden="true" />
      {name != null && <span className="text-primary">{name}: </span>}
      <span className={`ms-1 ${TYPE_COLOR[type] || ''}`}>
        {type === 'string' ? `"${value}"` : String(value)}
      </span>
    </div>
  );
}

/** Recursively renders an object, array, or primitive as a collapsible node. */
function JsonNode({ name, data }) {
  const [open, setOpen] = useState(true);

  if (data === null) return <JsonPrimitive name={name} value="null" type="null" />;

  const isArray = Array.isArray(data);
  if (isArray || typeof data === 'object') {
    const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);
    const label = isArray ? `Array[${entries.length}]` : `Object{${entries.length}}`;

    return (
      <div>
        <div
          className="d-flex"
          role="button"
          tabIndex={0}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen((v) => !v)}
          style={{ cursor: 'pointer' }}
        >
          <i
            className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`}
            style={{ width: CARET_WIDTH, flexShrink: 0, fontSize: '0.7em' }}
            aria-hidden="true"
          />
          {name != null && <span className="text-primary">{name}: </span>}
          <span className="ms-1 text-muted">{label}</span>
        </div>
        {open && (
          <div className="ms-3 border-start ps-2">
            {entries.map(([key, value]) => (
              <JsonNode key={key} name={key} data={value} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return <JsonPrimitive name={name} value={data} type={typeof data} />;
}

/**
 * @param {Object} props
 * @param {string|Object|Array|null} props.value - JSON string, or an already-parsed
 *   value (JSON columns come back from Sequelize deserialised).
 */
export default function JsonViewer({ value }) {
  if (value === null || value === undefined || value === '') {
    return <p className="text-muted small fst-italic mb-0">(empty)</p>;
  }

  let parsed;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      // Not valid JSON — show as-is, same as the previous plain <pre> behaviour
      return (
        <pre className="bg-white border rounded p-2 small mb-0" style={{ maxHeight: 200, overflow: 'auto' }}>
          {value}
        </pre>
      );
    }
  } else {
    parsed = value;
  }

  return (
    <div
      className="bg-white border rounded p-2 small font-monospace mb-0"
      style={{ maxHeight: 200, overflow: 'auto' }}
    >
      <JsonNode data={parsed} />
    </div>
  );
}
